import { useMemo } from "react";
import clsx from "clsx";
import { Slider } from "@radix-ui/themes";
import { generateValues } from "@/domains/hotel/helpers/generateValues";

type Props = {
  value: number;
  values: number[];
  min: number;
  max: number;
  step: number;
  displayStep?: number;
  className?: string;
  classNameSlider?: string;
  classNameLabel?: string;
  highlightActive?: boolean;
  onChange: (value: number) => void;
};

export default function FeeSelectDaysSlider({
  value,
  values,
  min,
  max,
  step,
  displayStep,
  className,
  classNameSlider,
  classNameLabel,
  highlightActive,
  onChange,
}: Props) {
  const displayValues = useMemo(
    () => (displayStep ? generateValues(max, displayStep) : null),
    [displayStep, max],
  );

  const width = 100 / (values.length - 1);

  return (
    <div className={clsx("flex flex-col w-full", className)}>
      <div className="relative">
        <Slider
          variant="soft"
          value={[value]}
          min={min}
          max={max}
          step={step}
          onValueChange={(v: number[]) => {
            const newValue = v[0];
            onChange(newValue);
          }}
          className={clsx("cursor-pointer", classNameSlider)}
        />
      </div>
      <div className={clsx("flex items-center mt-1")}>
        {values.map((item, index) => {
          const isEdges = index === 0 || index === values.length - 1;
          const isActive = item <= value;
          const isHidden = displayValues ? !displayValues.includes(item) : false;

          return (
            <div
              key={item}
              className={clsx(
                "flex flex-col gap-1.5 cursor-pointer text-11 font-bold",
                {
                  "items-start": index === 0,
                  "items-end": index === values.length - 1,
                  "items-center": !isEdges,
                  invisible: isHidden,
                },
                classNameLabel,
              )}
              style={{
                width: isEdges ? `${width / 2}%` : `${width}%`,
              }}
            >
              <div
                className={clsx("h-[0.375rem] w-[1px]", {
                  "bg-accent-solid": highlightActive && isActive,
                  "bg-[#191919]": highlightActive && !isActive,
                  "bg-white/10": !highlightActive,
                })}
              />
              <span
                className={clsx({
                  "text-white": !highlightActive || isActive,
                  "text-white/20": highlightActive && !isActive,
                })}
              >
                {item}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
